/**
 * MessageComposer — botón + modal para enviar mensajes formales al Super Admin.
 * Usado por Institutional Admin. Bidireccional: si `replyTo` viene definido,
 * hereda thread_id y funciona como respuesta.
 */
import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Send, X, Loader2, Paperclip, ImageIcon } from "lucide-react";

const MAX_ATTACHMENTS = 3;
const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024; // 2 MB
const ACCEPT_MIME = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const CATEGORIES = [
  { value: "bug", label: "🐞 Reporte de bug" },
  { value: "feature", label: "✨ Solicitud de feature" },
  { value: "support", label: "💬 Consulta general" },
  { value: "students", label: "👥 Problema con estudiantes" },
  { value: "billing", label: "💰 Facturación / comercial" },
  { value: "curriculum", label: "📚 Currículo / pedagogía" },
  { value: "other", label: "🔧 Otro" },
];

export const PRIORITIES = [
  { value: "low", label: "🟢 Baja" },
  { value: "medium", label: "🟡 Media" },
  { value: "high", label: "🟠 Alta" },
  { value: "urgent", label: "🔴 Urgente" },
];

export default function MessageComposer({ triggerLabel = "Enviar mensaje al Super Admin", parentId = null, toUserId = null, defaultSubject = "", onSent }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("support");
  const [priority, setPriority] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]); // [{ filename, mime_type, data, size, preview }]

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file
    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      toast.error(`Máximo ${MAX_ATTACHMENTS} imágenes por mensaje`);
      return;
    }
    const newOnes = [];
    for (const f of files) {
      if (!ACCEPT_MIME.includes(f.type)) {
        toast.error(`Formato no permitido: ${f.name}`);
        continue;
      }
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${f.name} supera 2 MB`);
        continue;
      }
      try {
        const dataUrl = await readFileAsDataUrl(f);
        const base64 = dataUrl.split(",")[1] || "";
        newOnes.push({
          filename: f.name,
          mime_type: f.type,
          data: base64,
          size: f.size,
          preview: dataUrl,
        });
      } catch {
        toast.error(`No se pudo leer ${f.name}`);
      }
    }
    if (newOnes.length) setAttachments((prev) => [...prev, ...newOnes]);
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (subject.trim().length < 3 || body.trim().length < 5) {
      toast.error("Asunto y cuerpo son obligatorios");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        subject: subject.trim(),
        body: body.trim(),
        category,
        priority,
        parent_id: parentId,
        to_user_id: toUserId,
      };
      if (attachments.length) {
        payload.attachments = attachments.map((a) => ({
          filename: a.filename,
          mime_type: a.mime_type,
          data: a.data,
          size: a.size,
        }));
      }
      const res = await axios.post(
        `${API}/messages`,
        payload,
        { withCredentials: true, timeout: 45000 },
      );
      toast.success("Mensaje enviado");
      setOpen(false);
      setSubject("");
      setBody("");
      setCategory("support");
      setPriority("medium");
      setAttachments([]);
      if (onSent) onSent(res.data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar el mensaje");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        data-testid="open-composer-btn"
        className="gap-2"
      >
        <MessageSquare className="w-4 h-4" />
        {triggerLabel}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
          onClick={() => !loading && setOpen(false)}
          data-testid="message-composer-modal"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 p-4">
              <div>
                <h3 className="font-bold text-gray-900">{parentId ? "Responder mensaje" : "Nuevo mensaje al Super Admin"}</h3>
                <p className="text-xs text-gray-500">Recibirás una copia por email cuando te respondan.</p>
              </div>
              <button onClick={() => !loading && setOpen(false)} className="p-1 rounded-full hover:bg-gray-100" data-testid="close-composer-btn">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Categoría</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="composer-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Prioridad</label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger data-testid="composer-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 font-medium">Asunto</label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ej: Necesito habilitar el módulo de matemática para 9°"
                  maxLength={180}
                  data-testid="composer-subject"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 font-medium">Mensaje</label>
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Describe con claridad lo que necesitas o el problema que estás viendo…"
                  maxLength={5000}
                  rows={6}
                  data-testid="composer-body"
                />
                <p className="text-xs text-gray-400 mt-1">{body.length}/5000</p>
              </div>

              {/* Attachments — evidencias fotográficas */}
              <div>
                <label className="text-xs text-gray-500 font-medium flex items-center gap-1">
                  <ImageIcon className="w-3.5 h-3.5" />
                  Adjuntar evidencias (imágenes)
                  <span className="text-gray-400 font-normal">
                    · máx {MAX_ATTACHMENTS} archivos · 2 MB c/u
                  </span>
                </label>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <label
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition ${
                      attachments.length >= MAX_ATTACHMENTS
                        ? "bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                    data-testid="composer-attach-label"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    Elegir imágenes
                    <input
                      type="file"
                      accept={ACCEPT_MIME.join(",")}
                      multiple
                      onChange={onFilesSelected}
                      disabled={attachments.length >= MAX_ATTACHMENTS || loading}
                      className="hidden"
                      data-testid="composer-attach-input"
                    />
                  </label>
                  <span className="text-xs text-gray-400">
                    {attachments.length}/{MAX_ATTACHMENTS}
                  </span>
                </div>

                {attachments.length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="composer-attachments-preview">
                    {attachments.map((a, i) => (
                      <div key={i} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                        <img
                          src={a.preview}
                          alt={a.filename}
                          className="w-full h-24 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(i)}
                          disabled={loading}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          data-testid={`composer-remove-attachment-${i}`}
                          aria-label="Quitar"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <p className="text-[10px] text-gray-500 px-1 py-0.5 truncate bg-white/90 border-t border-gray-100">
                          {a.filename}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100 p-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>Cancelar</Button>
              <Button
                onClick={submit}
                disabled={loading}
                className="bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white gap-2"
                data-testid="composer-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar mensaje
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
